import { CrossIcon, LinkBanner, MenuIcon } from '@/components';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  ThemedComponentWrapper,
  useIsMobile,
  useToggle
} from '@blockanalitica/ui';
import { ReactNode, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Footer from './Footer';
import NavigationItem from './NavigationItem';
// @ts-ignore
import SkyLogo from '@/assets/logos/sky_logo.svg?react';

export type NavigationData = {
  title: string;
  linkTo?: string;
  icon?: ReactNode;
  subMenu?: NavigationData[];
};

export interface SidebarProps extends FlexProps {
  $closed: boolean;
  $isMobile?: boolean;
}

export interface LayoutProps {
  children?: ReactNode;
}

const ContentContainer = styled(Flex)<FlexProps>`
  flex: 1 1 auto;
  box-sizing: border-box;
  padding: ${(props) => props.theme.space['s-m']};
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
`;

const SidebarButton = styled(Flex)<FlexProps>`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const SidebarContainer = styled(Flex)<SidebarProps>`
  width: 300px;
  position: fixed;

  top: 0;
  bottom: 0;
  right: 0;

  z-index: 50;
  flex-direction: column;
  height: 97vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: all 0.3s;
  visibility: visible;
  margin-left: 0;

  ${(props) => css`
    border-right: 1px solid ${props.theme.colors.onSurface};
    background-color: ${props.theme.colors.black65};
    padding: ${props.theme.space.s};
  `}

  ${(props) =>
    props.$isMobile
      ? css`
          left: ${props.theme.space.s};
          right: ${props.theme.space.s};
          top: ${props.theme.space.l};
          bottom: auto;
          width: auto;
          max-height: 70vh;
          transition: all 0.3s;
          scrollbar-width: none;
          border: 1px solid ${props.theme.colors.onSurface};
          border-radius: ${props.theme.sizes['-1']};
          background-color: ${props.theme.colors.black65};
          overflow-y: auto;
        `
      : null}

  ${(props) =>
    props.$closed
      ? props.$isMobile
        ? css`
            visibility: hidden;
            margin-top: -70vh;
          `
        : css`
            visibility: hidden;
            margin-right: -340px;
          `
      : null}
`;

const CloseButton = styled(Box)<BoxProps>`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.linkHover};
  }
`;

const AlphaBackgroundLayer = styled.div`
  display: block;
  position: fixed;
  inset: 0;
  background-color: ${(props) => props.theme.colors.black65};
  z-index: 49;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const navigationData: NavigationData[] = [
  { title: 'Home', linkTo: '/', icon: undefined },
  {
    title: 'Sky',
    subMenu: [
      { title: 'Collateral', linkTo: '/collateral' },
      { title: 'Supply', linkTo: '/supply' },
      { title: 'Financials', linkTo: '/financials' },
      { title: 'Expenses', linkTo: '/expenses' },
      { title: 'Buyback', linkTo: '/buyback' },
      { title: 'Aggregate Backstop Capital', linkTo: '/capital-backstop' }
    ]
  },
  {
    title: 'Stars',
    subMenu: [
      { title: 'Required Risk Capital', linkTo: '/required-risk-capital' }
    ]
  },
  {
    title: 'Tokens',
    subMenu: [
      { title: 'Upgrade', linkTo: '/upgrade' },
      { title: 'Multichain', linkTo: '/multichain' },
      { title: 'Tokens', linkTo: '/tokens' }
    ]
  },
  {
    title: 'Risk',
    subMenu: [{ title: 'Parameters', linkTo: '/parameters' }]
  },
  { title: 'Glossary', linkTo: '/glossary' }
];

// @ts-ignore
if (import.meta.env.VITE_PUBLIC_RESTRICTED_BUILD !== 'true') {
  navigationData.splice(navigationData.length - 3, 0, {
    title: 'Yield',
    subMenu: [
      {
        title: 'stUSDS',
        linkTo: '/stusds'
      },
      {
        title: 'Rewards',
        linkTo: '/rewards'
      },
      {
        title: 'Savings',
        linkTo: '/savings'
      },
      {
        title: 'Staking',
        linkTo: '/staking'
      }
    ]
  });
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, toggleSidebar] = useToggle();
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    toggleSidebar(false);
  }, [location.pathname, toggleSidebar]);

  return (
    <>
      <SidebarContainer
        flexDirection="column"
        gap="m"
        $closed={!sidebarOpen}
        $isMobile={isMobile}>
        <Flex justifyContent={'space-between'}>
          <Link to="/">
            <SkyLogo height={isMobile ? 24 : 32} alt="Sky" />
          </Link>

          <CloseButton alignSelf="center" onClick={() => toggleSidebar(false)}>
            <CrossIcon size={14} />
          </CloseButton>
        </Flex>

        <Flex
          as="nav"
          role="navigation"
          aria-label="Sidebar"
          flexDirection="column"
          justifyContent="flex-start"
          gap="3xs">
          {navigationData.map((item, index) =>
            item.subMenu && item.subMenu.length > 0 ? (
              <Box
                key={item.title}
                display="flex"
                flexDirection="column"
                gap="2xs"
                paddingBottom="xs"
                borderBottom="1px solid"
                borderColor="border15">
                <ThemedComponentWrapper name="listHeader" variant="title">
                  {item.title}
                </ThemedComponentWrapper>
                <Flex role="menu" flexDirection="column" gap="3xs">
                  {item.subMenu.map((child) => (
                    <NavigationItem
                      variant="secondary"
                      key={child.title}
                      item={child}
                    />
                  ))}
                </Flex>
              </Box>
            ) : (
              <NavigationItem
                key={item.title}
                item={item}
                variant={
                  index === 0
                    ? 'home'
                    : index === navigationData.length - 1
                      ? 'glossary'
                      : 'primary'
                }
              />
            )
          )}
        </Flex>
      </SidebarContainer>

      <ContentContainer
        gap="m"
        flexDirection="column"
        flexBasis={['auto', 'auto', '100%']}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          padding={['0', '0 s']}>
          <Link to="/">
            <SkyLogo height={isMobile ? 32 : 36} alt="Sky" />
          </Link>
          <SidebarButton>
            <MenuIcon
              role="button"
              aria-label="Open sidebar button"
              onClick={() => toggleSidebar()}
              size={20}
            />
          </SidebarButton>
        </Flex>
        <Flex>
          <LinkBanner
            text="financial.skyeco.com"
            linkTo="https://financial.skyeco.com/">
            <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
              Independent analytics for education only. Not an offer or
              promotion of any cryptoasset or service; no accounts or
              transactions available on this site. Figures derive from on-chain
              data and models and are variable and not guaranteed.
              <br />
              <br />
              Detailed financial data for the Sky Ecosystem is available on Sky
              Ecosystem Financial Insights.
            </Box>
          </LinkBanner>
        </Flex>

        <main>{children ? children : <Outlet />}</main>

        <Footer />
      </ContentContainer>

      {sidebarOpen ? (
        <AlphaBackgroundLayer onClick={() => toggleSidebar()} />
      ) : null}
    </>
  );
}
